/**
 * APPROVING A PROGRAM THAT IS NOT A BROWSER.
 *
 * śikṣāmitra — and anything else built on this account later — sends its user
 * here rather than asking for a password of its own. This page is the whole
 * safety of that arrangement, so it does two things very deliberately:
 *
 *   IT SHOWS WHAT IS ASKING, by name, before anything is approved. An approval
 *   screen that says only "allow?" teaches people to press yes.
 *
 *   IT SHOWS THE CODE ON BOTH SCREENS. The person compares what the editor is
 *   displaying with what is printed here. That comparison is what stops
 *   somebody being talked into approving a code that is not theirs — the one
 *   attack this flow actually has, and the reason the code is read aloud in
 *   letters that cannot be confused (see server/lib/device-auth.ts).
 *
 * Signing in is not required to LOOK at a code, only to approve one, so
 * somebody who followed the link before logging in sees what they are about to
 * approve and is sent to sign in with the code kept.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { EditorialAuthShell } from '../components/auth/EditorialAuthShell';
import { apiFetch, ApiError } from '../lib/api';
import { useMe } from '../lib/auth';

interface Waiting {
  clientName: string;
  expiresAt: string;
}

/** The alphabet has no vowels and no 0/1/O/I — see the server. */
const tidy = (raw: string): string => {
  const bare = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
  return bare.length > 4 ? `${bare.slice(0, 4)}-${bare.slice(4)}` : bare;
};

type Outcome = 'allowed' | 'denied' | null;

export default function LinkDevicePage() {
  const me = useMe();
  const navigate = useNavigate();
  const location = useLocation();

  const fromUrl = useMemo(
    () => tidy(new URLSearchParams(location.search).get('code') ?? ''),
    [location.search],
  );

  const [code, setCode] = useState(fromUrl);
  const [waiting, setWaiting] = useState<Waiting | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>(null);

  const look = useCallback(async (value: string) => {
    setProblem(null);
    setWaiting(null);
    if (value.length < 9) return;
    try {
      const found = await apiFetch<Waiting>(`/api/auth/device/${encodeURIComponent(value)}`);
      setWaiting(found);
    } catch (err) {
      setProblem(
        err instanceof ApiError && err.status === 404
          ? 'That code is not waiting for anyone. It may have expired — ask the program for a new one.'
          : 'Could not reach Veda Union just now.',
      );
    }
  }, []);

  /* A code in the link is looked up straight away: somebody who clicked
     "open in browser" in the editor should not have to type it again. */
  useEffect(() => { if (fromUrl.length === 9) void look(fromUrl); }, [fromUrl, look]);

  const decide = async (decision: 'allow' | 'deny') => {
    setBusy(true);
    setProblem(null);
    try {
      await apiFetch('/api/auth/device/decide', {
        method: 'POST',
        body: JSON.stringify({ code, decision }),
      });
      setOutcome(decision === 'allow' ? 'allowed' : 'denied');
    } catch (err) {
      setProblem(
        err instanceof ApiError && err.status === 404
          ? 'That code is no longer waiting. It may have expired.'
          : 'Could not reach Veda Union just now.',
      );
    } finally {
      setBusy(false);
    }
  };

  const signedIn = me.data?.user != null;

  return (
    <EditorialAuthShell
      eyebrow="Connect an application"
      headline={<>Let it <span className="font-display-italic" style={{ color: 'var(--color-violet)' }}>in</span>.</>}
      lede={
        outcome === null
          ? 'A program has asked to use your Veda Union account. Check that the code below is the one it is showing you.'
          : undefined
      }
      verse={{
        deva: 'सत्यमेव जयते',
        iast: 'Satyam eva jayate',
        english: 'Truth alone prevails.',
        source: 'Muṇḍaka Upaniṣad 3.1.6',
      }}
    >
      {outcome === 'allowed' && (
        <div>
          <p className="font-display text-[1.35rem] leading-[1.4] text-[var(--color-ink)]">
            Connected. You can go back to it now.
          </p>
          <p className="text-[0.97rem] leading-[1.55] text-[var(--color-ink-soft)] mt-3 max-w-[40ch]">
            It signs in as you, with exactly the permissions your account has —
            no more. You can disconnect it at any time by changing your
            password, which signs out every device.
          </p>
          <div className="auth-links"><Link to="/dashboard">← Back to your dashboard</Link></div>
        </div>
      )}

      {outcome === 'denied' && (
        <div>
          <p className="font-display text-[1.35rem] leading-[1.4] text-[var(--color-ink)]">
            Refused. Nothing was connected.
          </p>
          <p className="text-[0.97rem] leading-[1.55] text-[var(--color-ink-soft)] mt-3 max-w-[40ch]">
            If you were not expecting this, you did the right thing — and
            nobody learned anything about your account.
          </p>
          <div className="auth-links"><Link to="/dashboard">← Back to your dashboard</Link></div>
        </div>
      )}

      {outcome === null && (
        <form onSubmit={(e) => { e.preventDefault(); void look(code); }} noValidate>
          <label className="block">
            <span className="text-[0.8rem] uppercase tracking-[0.1em] text-[var(--color-ink-soft)]">
              The code it is showing
            </span>
            <input
              className="auth-input mt-2 font-mono text-[1.6rem] tracking-[0.22em] uppercase"
              value={code}
              autoFocus
              inputMode="text"
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              placeholder="XXXX-XXXX"
              aria-label="The code the program is showing"
              onChange={(e) => {
                const next = tidy(e.target.value);
                setCode(next);
                if (next.length === 9) void look(next);
                else { setWaiting(null); setProblem(null); }
              }}
            />
          </label>

          {problem !== null && (
            <p className="text-[0.95rem] mt-3 text-[var(--color-rose,#b3261e)]">{problem}</p>
          )}

          {waiting !== null && (
            <div className="mt-6 rounded-lg border border-[var(--color-rule)] p-4">
              <p className="text-[0.8rem] uppercase tracking-[0.1em] text-[var(--color-ink-soft)]">
                Asking for access
              </p>
              <p className="font-display text-[1.3rem] mt-1 text-[var(--color-ink)]">
                {waiting.clientName}
              </p>
              <p className="text-[0.92rem] leading-[1.55] text-[var(--color-ink-soft)] mt-3">
                It will be able to do everything your account can do. Only allow
                this if you started it yourself, and only if <strong>{code}</strong>{' '}
                is the code on its screen.
              </p>
            </div>
          )}

          {waiting !== null && !signedIn && (
            <div className="auth-actions">
              {/* Kept in the URL, so signing in returns to exactly this code. */}
              <button
                type="button"
                className="btn-primary auth-submit"
                onClick={() => navigate(`/login?next=${encodeURIComponent(`/link?code=${code}`)}`)}
              >
                <span>Sign in to continue</span>
                <span className="btn-arrow-icon" aria-hidden>→</span>
              </button>
            </div>
          )}

          {waiting !== null && signedIn && (
            <div className="auth-actions flex gap-3">
              <button
                type="button"
                className="btn-primary auth-submit"
                disabled={busy}
                onClick={() => void decide('allow')}
              >
                <span>{busy ? 'Connecting…' : 'Allow'}</span>
              </button>
              <button
                type="button"
                className="btn-ghost auth-submit"
                disabled={busy}
                onClick={() => void decide('deny')}
              >
                <span>Not me — refuse</span>
              </button>
            </div>
          )}

          {waiting === null && problem === null && (
            <p className="text-[0.92rem] leading-[1.55] text-[var(--color-ink-soft)] mt-4 max-w-[44ch]">
              Type the eight letters the program is showing. Veda Union will
              never ask you for this code by email or by telephone.
            </p>
          )}
        </form>
      )}
    </EditorialAuthShell>
  );
}
